import { LocalDraft } from '@business/entities/common';

// Drafts older than this are considered stale and dropped on load.
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
// Oldest drafts are dropped beyond this count.
const DEFAULT_MAX_DRAFTS = 10;

export interface LocalDraftStoreOptions {
  // Whether a value is worth keeping as a draft; defaults to "any non-empty field".
  hasContent?(value: Record<string, any>): boolean;
  maxDrafts?: number;
}

export interface LocalDraftStore {
  load(): LocalDraft[];
  upsert(id: string, value: Record<string, any>): void;
  remove(id: string): void;
}

export function newLocalDraftId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// Deep scan: any non-empty string, number, checked flag or non-empty nested value counts as content.
export function hasAnyDraftContent(value: any): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return true;
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.some(hasAnyDraftContent);
  if (typeof value === 'object') return Object.values(value).some(hasAnyDraftContent);
  return false;
}

export function createLocalDraftStore(storageKey: string, options?: LocalDraftStoreOptions): LocalDraftStore {
  const hasContent = options?.hasContent ?? hasAnyDraftContent;
  const maxDrafts = options?.maxDrafts ?? DEFAULT_MAX_DRAFTS;

  const persist = (drafts: LocalDraft[]) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(drafts));
    } catch {
      // localStorage full or unavailable — draft saving is best-effort, never break the form.
    }
  };

  const load = (): LocalDraft[] => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [];

      const drafts = JSON.parse(raw) as LocalDraft[];
      if (!Array.isArray(drafts)) return [];

      const fresh = drafts
        .filter(d => d && d.id && d.value && d.savedAt && Date.now() - d.savedAt <= DRAFT_MAX_AGE_MS)
        .sort((a, b) => b.savedAt - a.savedAt);

      if (fresh.length !== drafts.length) persist(fresh);

      return fresh;
    } catch {
      return [];
    }
  };

  return {
    load,

    upsert(id: string, value: Record<string, any>) {
      if (!hasContent(value)) return;

      const draft: LocalDraft = { id, value, savedAt: Date.now() };
      persist([draft, ...load().filter(d => d.id !== id)].slice(0, maxDrafts));
    },

    remove(id: string) {
      persist(load().filter(d => d.id !== id));
    },
  };
}
